"use client";

import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { FlagRuleSelect } from "./FlagRuleSelect";
import type { GroupRule } from "@/lib/api/groupRules";

/**
 * "Er du sikker?" for the ⋮-menu's hide and delete (OSO-663) — the app's own
 * dialog where `window.confirm` used to be, and the place a moderator cites
 * the group rule the content broke. The copy lives HERE, once, keyed on what
 * is being removed and how; `PostCard` and `CommentItem` render the same
 * dialog rather than each keeping their own Danish.
 *
 * `moderating` is "a moderator removing somebody else's content" — the one
 * case the server notifies the author in — so it gates both the notice
 * saying so and the rule picker (a citation with no notification to carry it
 * is the pointlessness OSO-648 deliberately avoided).
 */
const COPY = {
  post: {
    delete: {
      title: "Slet opslag?",
      description: "Er du sikker på, at du vil slette dette opslag?",
    },
    hide: {
      title: "Skjul opslag?",
      description:
        "Opslaget skjules for alle andre end forfatteren og gruppens moderatorer.",
    },
  },
  comment: {
    delete: {
      title: "Slet kommentar?",
      description: "Er du sikker på, at du vil slette denne kommentar?",
    },
    hide: {
      title: "Skjul kommentar?",
      description:
        "Kommentaren skjules for alle andre end forfatteren og gruppens moderatorer.",
    },
  },
} as const;

export type RemovalAction = "delete" | "hide";

export function RemoveContentDialog({
  action,
  kind,
  moderating,
  rules,
  rulesLoading = false,
  ruleId,
  setRuleId,
  pending,
  error,
  onConfirm,
  onCancel,
}: {
  /** Which removal is awaiting confirmation; null renders nothing. */
  action: RemovalAction | null;
  kind: "post" | "comment";
  /** A moderator removing somebody else's content — shows the notification
   *  notice and the rule picker. An author deleting their own gets the plain
   *  confirm. */
  moderating: boolean;
  rules: GroupRule[];
  /** The list is still being fetched — say so rather than rendering the
   *  no-rules shape, which is what an empty list otherwise looks like. */
  rulesLoading?: boolean;
  ruleId: GroupRule["id"] | null;
  setRuleId: (val: GroupRule["id"] | null) => void;
  pending: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!action) return null;
  const copy = COPY[kind][action];
  const showBody = moderating || error !== null;

  return (
    <ConfirmDialog
      open
      title={copy.title}
      description={copy.description}
      confirmLabel={action === "delete" ? "Slet" : "Skjul"}
      pendingLabel={action === "delete" ? "Sletter…" : "Skjuler…"}
      variant={action === "delete" ? "destructive" : "primary"}
      pending={pending}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      {showBody && (
        <>
          {moderating && (
            <p className="text-sm text-muted-foreground">
              Forfatteren får besked om afgørelsen.
              {(rules.length > 0 || rulesLoading) &&
                " Du kan henvise til den regel, der er brudt."}
            </p>
          )}
          {moderating &&
            (rulesLoading ? (
              <p className="text-sm text-subtle-foreground">Henter gruppens regler…</p>
            ) : (
              <FlagRuleSelect
                rules={rules}
                value={ruleId}
                onChange={setRuleId}
                placeholder="Henvis til en regel (valgfrit)"
                ariaLabel="Hvilken regel er brudt?"
                className="w-full rounded border border-border-strong bg-surface text-foreground px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            ))}
          {error && <p className="text-sm text-destructive">{error}</p>}
        </>
      )}
    </ConfirmDialog>
  );
}
